import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { useSelector } from 'react-redux';
import config from '../config';
import selectedEnvironment from '../env';
import AuthService from '../Services/auth.service';

const HUB_URL = `${config[selectedEnvironment].API_URL.replace(/\/api\/?$/, '')}/presenceHub`;

const EMPTY_PRESENCE = { viewers: [], otherViewers: [] };

const CasePresenceContext = createContext(EMPTY_PRESENCE);

export const useCasePresence = () => useContext(CasePresenceContext);

/**
 * Reports which other users currently have the selected client open, so screens can warn about two
 * people working the same case. The joined case follows the selected client in Redux, which means
 * every client screen is covered without each one having to opt in.
 */
export const CasePresenceProvider = ({ children }) => {
    const caseId = useSelector(state => state.selectedClientSlice?.caseId) ?? null;

    const [viewers, setViewers] = useState([]);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [isConnected, setIsConnected] = useState(false);

    const connectionRef = useRef(null);
    const caseIdRef = useRef(null);

    useEffect(() => {
        let cancelled = false;

        AuthService.getCurrentUserTypeId()
            .then(id => { if (!cancelled) setCurrentUserId(id ?? null); })
            .catch(() => { /* Without an identity we cannot tell ourselves apart, so stay silent. */ });

        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let connection;
        let disposed = false;

        const start = async () => {
            // Only a logged-out check; the factory below re-reads the token on every connect so a
            // reconnect after a token refresh does not authenticate with a stale one.
            const token = await AuthService.getCurrentToken();
            if (!token || disposed) return;

            connection = new HubConnectionBuilder()
                .withUrl(HUB_URL, { accessTokenFactory: () => AuthService.getCurrentToken() })
                .withAutomaticReconnect([0, 2000, 5000, 10000])
                .configureLogging(LogLevel.Warning)
                .build();

            connection.on('CaseViewersChanged', (incomingCaseId, incomingViewers) => {
                // A broadcast for the case we just left can still be in flight while navigating.
                if (incomingCaseId !== caseIdRef.current) return;
                setViewers(Array.isArray(incomingViewers) ? incomingViewers : []);
            });

            // A reconnect gets a new connection id, so the server no longer knows our case.
            connection.onreconnected(() => {
                if (caseIdRef.current) {
                    connection.invoke('JoinCase', caseIdRef.current).catch(() => { });
                }
            });

            connection.onclose(() => setIsConnected(false));

            try {
                await connection.start();
                if (disposed) {
                    await connection.stop();
                    return;
                }
                connectionRef.current = connection;
                setIsConnected(true);
            } catch {
                // Presence is advisory: failing to connect simply means no warning is shown.
            }
        };

        start();

        return () => {
            disposed = true;
            connectionRef.current = null;
            setIsConnected(false);
            connection?.stop();
        };
    }, []);

    useEffect(() => {
        caseIdRef.current = caseId;
        // Never carry the previous case's viewers into the next one.
        setViewers([]);

        const connection = connectionRef.current;
        if (!connection || connection.state !== HubConnectionState.Connected) return;

        const announce = caseId
            ? connection.invoke('JoinCase', caseId)
            : connection.invoke('LeaveCase');

        announce.catch(() => { });
    }, [caseId, isConnected]);

    const value = useMemo(() => {
        if (!currentUserId) return { viewers, otherViewers: [] };

        return {
            viewers,
            otherViewers: viewers.filter(viewer => viewer.userId !== currentUserId),
        };
    }, [viewers, currentUserId]);

    return (
        <CasePresenceContext.Provider value={value}>
            {children}
        </CasePresenceContext.Provider>
    );
};
